// src/actions/upActions.js
import axios from 'axios';
import {
  GET_ALL_UP_RECORDS_REQUEST,
  GET_ALL_UP_RECORDS_SUCCESS,
  GET_ALL_UP_RECORDS_FAIL,
  IMPORT_UP_RECORDS_REQUEST,
  IMPORT_UP_RECORDS_SUCCESS,
  IMPORT_UP_RECORDS_FAIL,
  EXPORT_UP_RECORDS_REQUEST,
  EXPORT_UP_RECORDS_SUCCESS,
  EXPORT_UP_RECORDS_FAIL,
  UPDATE_UP_RECORD_REQUEST,
  UPDATE_UP_RECORD_SUCCESS,
  UPDATE_UP_RECORD_FAIL,
  DELETE_UP_RECORD_REQUEST,
  DELETE_UP_RECORD_SUCCESS,
  DELETE_UP_RECORD_FAIL,
  BULK_DELETE_UP_RECORDS_REQUEST,
  BULK_DELETE_UP_RECORDS_SUCCESS,
  BULK_DELETE_UP_RECORDS_FAIL,
} from '../constants/upConstants';
import { logout } from './userActions';

const baseUrl = process.env.REACT_APP_API_URL;

// Get all UP records with search and pagination
export const getAllUpRecords = (queryParams) => async (dispatch, getState) => {
  try {
    dispatch({ type: GET_ALL_UP_RECORDS_REQUEST });

    const {
      userLogin: { userInfo },
    } = getState();

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userInfo.token}`,
      },
    };

    const { data } = await axios.get(`${baseUrl}/up${queryParams ? `?${queryParams}` : ''}`, config);

    dispatch({
      type: GET_ALL_UP_RECORDS_SUCCESS,
      payload: data,
    });
  } catch (error) {
    if (error.response?.status === 401) {
      dispatch(logout());
      return;
    }
    dispatch({
      type: GET_ALL_UP_RECORDS_FAIL,
      payload: error.response && error.response.data.message
        ? error.response.data.message
        : error.message,
    });
  }
};

// Import UP records from file
export const importUpRecords = (formData) => async (dispatch, getState) => {
  try {
    dispatch({ type: IMPORT_UP_RECORDS_REQUEST });

    const {
      userLogin: { userInfo },
    } = getState();

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${userInfo.token}`,
      },
    };

    const { data } = await axios.post(`${baseUrl}/up/import`, formData, config);

    dispatch({
      type: IMPORT_UP_RECORDS_SUCCESS,
      payload: data,
    });
  } catch (error) {
    if (error.response?.status === 401) {
      dispatch(logout());
      return;
    }
    dispatch({
      type: IMPORT_UP_RECORDS_FAIL,
      payload: error.response && error.response.data.message
        ? error.response.data.message
        : error.message,
    });
  }
};

// Export UP records
export const exportUpRecords = () => async (dispatch, getState) => {
  try {
    dispatch({ type: EXPORT_UP_RECORDS_REQUEST });

    const {
      userLogin: { userInfo },
    } = getState();

    const config = {
      headers: {
        Authorization: `Bearer ${userInfo.token}`,
      },
      responseType: 'blob',
    };

    const { data } = await axios.get(`${baseUrl}/up/export`, config);

    // Create download link
    const url = URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'UP_Export.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);

    dispatch({
      type: EXPORT_UP_RECORDS_SUCCESS,
      payload: 'Export completed successfully',
    });
  } catch (error) {
    if (error.response?.status === 401) {
      dispatch(logout());
      return;
    }
    dispatch({
      type: EXPORT_UP_RECORDS_FAIL,
      payload: error.response && error.response.data.message
        ? error.response.data.message
        : error.message,
    });
  }
};

// Update UP record
export const updateUpRecord = (id, recordData) => async (dispatch, getState) => {
  try {
    dispatch({ type: UPDATE_UP_RECORD_REQUEST });

    const {
      userLogin: { userInfo },
    } = getState();

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userInfo.token}`,
      },
    };

    const { data } = await axios.put(`${baseUrl}/up/${id}`, recordData, config);

    dispatch({
      type: UPDATE_UP_RECORD_SUCCESS,
      payload: data,
    });
  } catch (error) {
    if (error.response?.status === 401) {
      dispatch(logout());
      return;
    }
    dispatch({
      type: UPDATE_UP_RECORD_FAIL,
      payload: error.response && error.response.data.message
        ? error.response.data.message
        : error.message,
    });
  }
};

// Delete UP record
export const deleteUpRecord = (id) => async (dispatch, getState) => {
  try {
    dispatch({ type: DELETE_UP_RECORD_REQUEST });

    const {
      userLogin: { userInfo },
    } = getState();

    const config = {
      headers: {
        Authorization: `Bearer ${userInfo.token}`,
      },
    };

    await axios.delete(`${baseUrl}/up/${id}`, config);

    dispatch({
      type: DELETE_UP_RECORD_SUCCESS,
    });
  } catch (error) {
    if (error.response?.status === 401) {
      dispatch(logout());
      return;
    }
    dispatch({
      type: DELETE_UP_RECORD_FAIL,
      payload: error.response && error.response.data.message
        ? error.response.data.message
        : error.message,
    });
  }
};

// Bulk delete UP records
export const bulkDeleteUpRecords = (ids) => async (dispatch, getState) => {
  try {
    dispatch({ type: BULK_DELETE_UP_RECORDS_REQUEST });

    const {
      userLogin: { userInfo },
    } = getState();

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userInfo.token}`,
      },
    };

    const { data } = await axios.post(`${baseUrl}/up/bulk-delete`, { ids }, config);

    dispatch({
      type: BULK_DELETE_UP_RECORDS_SUCCESS,
      payload: { deletedCount: data.deletedCount },
    });

    return { deletedCount: data.deletedCount }; // Return the deleted count
  } catch (error) {
    if (error.response?.status === 401) {
      dispatch(logout());
      return;
    }
    dispatch({
      type: BULK_DELETE_UP_RECORDS_FAIL,
      payload: error.response && error.response.data.message
        ? error.response.data.message
        : error.message,
    });
    throw error; // Re-throw for handling in the component
  }
};


export const syncUpTaskStatus = () => async (dispatch, getState) => {
  try {
    dispatch({
      type: 'SYNC_UP_TASK_STATUS_REQUEST',
    });

    const {
      userLogin: { userInfo },
    } = getState();

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userInfo?.token}`,
      },
    };

    const { data } = await axios.post(
      `${baseUrl}/up/sync-status`,
      {},
      config
    );

    dispatch({
      type: 'SYNC_UP_TASK_STATUS_SUCCESS',
      payload: data,
    });

    return data; // Return data including updatedRecords
  } catch (error) {
    if (error.response?.status === 401) {
      dispatch(logout());
      return;
    }
    dispatch({
      type: 'SYNC_UP_TASK_STATUS_FAIL',
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    });
    throw error;
  }
};

export const getFieldExecutives = (search = '') => async (dispatch, getState) => {
  try {
    dispatch({
      type: 'GET_FIELD_EXECUTIVES_REQUEST',
    });

    const {
      userLogin: { userInfo },
    } = getState();

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userInfo?.token}`,
      },
    };

    const { data } = await axios.get(
      `${baseUrl}/up/field-executives?search=${encodeURIComponent(search)}`,
      config
    );

    dispatch({
      type: 'GET_FIELD_EXECUTIVES_SUCCESS',
      payload: data,
    });

    return data; // Return the data
  } catch (error) {
    if (error.response?.status === 401) {
      dispatch(logout());
      return;
    }
    dispatch({
      type: 'GET_FIELD_EXECUTIVES_FAIL',
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    });
    throw error; // Throw error so it can be caught
  }
};

export const getUpDepartments = (importDate = '') => async (dispatch, getState) => {
  try {
    dispatch({
      type: 'GET_UP_DEPARTMENTS_REQUEST',
    });

    const {
      userLogin: { userInfo },
    } = getState();

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userInfo?.token}`,
      },
    };

    let url = `${baseUrl}/up/departments`;
    if (importDate) {
      url += `?importDate=${encodeURIComponent(importDate)}`;
    }

    const { data } = await axios.get(url, config);

    dispatch({
      type: 'GET_UP_DEPARTMENTS_SUCCESS',
      payload: data,
    });

    return data;
  } catch (error) {
    if (error.response?.status === 401) {
      dispatch(logout());
      return;
    }
    dispatch({
      type: 'GET_UP_DEPARTMENTS_FAIL',
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    });
    throw error;
  }
};

export const getUpFOS = (importDate = '') => async (dispatch, getState) => {
  try {
    dispatch({
      type: 'GET_UP_FOS_REQUEST',
    });

    const {
      userLogin: { userInfo },
    } = getState();

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userInfo?.token}`,
      },
    };

    let url = `${baseUrl}/up/fos`;
    if (importDate) {
      url += `?importDate=${encodeURIComponent(importDate)}`;
    }

    const { data } = await axios.get(url, config);

    dispatch({
      type: 'GET_UP_FOS_SUCCESS',
      payload: data,
    });

    return data;
  } catch (error) {
    if (error.response?.status === 401) {
      dispatch(logout());
      return;
    }
    dispatch({
      type: 'GET_UP_FOS_FAIL',
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    });
    throw error;
  }
};
